import { useCallback } from "react";
import { Box, Button } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { InvoicesTable } from "../invoice/DashboardWidget/InvoicesTable";
import { BaseModalComponent } from "../common/BaseModalComponent";
import { InvoiceCreateView } from "../invoice/create/InvoiceCreateView";
import { JobSelectionForInvoice } from "./invoice-selection/JobSelectionForInvoice";
import { sortJobEventsByStartDate, type JobEventForInvoice } from "../jobs/jobs-details/events/board/context";
import type { ClientJob } from "./invoice-selection/actions/getClientJobs";
import type { InvoiceVisitGroupSelection } from "../invoice/create/types/invoiceVisitGroups";
import { useModals } from "../../context/ModalContext";
import { colors } from "../ui/colors";

interface ClientInvoicesTabProps {
  clientUid: string;
}

export const ClientInvoicesTab = ({ clientUid }: ClientInvoicesTabProps) => {
  const { openModal, closeByKey } = useModals();

  const invoiceCreateModalKey = `ClientInvoiceCreate-${clientUid}`;
  const jobSelectionModalKey = `ClientJobSelection-${clientUid}`;

  const handleContinueToInvoice = useCallback(
    (events: JobEventForInvoice[], job: ClientJob, visitGroups: InvoiceVisitGroupSelection[]) => {
      const sorted = sortJobEventsByStartDate(events);

      closeByKey(jobSelectionModalKey);
      openModal(
        { key: invoiceCreateModalKey, preventParentTrigger: true },
        <InvoiceCreateView
          jobUid={job.id}
          jobEvents={sorted}
          clientUid={clientUid}
          scopeOfWorkUid={job.scopeOfWorkUid ?? ""}
          eventSchedulerUid={job.eventUid ?? ""}
          invoiceModal={invoiceCreateModalKey}
          visitGroups={visitGroups}
        />,
        null,
      );
    },
    [clientUid, closeByKey, invoiceCreateModalKey, jobSelectionModalKey, openModal],
  );

  if (!clientUid) {
    return (
      <Box sx={{ p: 2 }}>
        <p>No client UID provided</p>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* New Invoice button */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, pt: 1 }}>
        <BaseModalComponent
          modalKey={jobSelectionModalKey}
          modalLabel="New Invoice"
          defaultSizes={{ width: { xs: "95%", md: "720px" }, height: { xs: "90%", md: "80%" } }}
          elementTrigger={
            <Button
              variant="contained"
              size="small"
              disableElevation
              startIcon={<AddRoundedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8125rem",
                borderRadius: "10px",
                px: 2,
                bgcolor: colors.orange[500],
                "&:hover": { bgcolor: colors.orange[600] },
              }}
            >
              New Invoice
            </Button>
          }
        >
          <JobSelectionForInvoice clientUid={clientUid} onContinue={handleContinueToInvoice} />
        </BaseModalComponent>
      </Box>

      {/* Invoice Create modal anchor (opened programmatically) */}
      <BaseModalComponent
        modalKey={invoiceCreateModalKey}
        modalLabel="Create Invoice"
        defaultSizes={{ width: "80%", height: "85%" }}
      >
        {null}
      </BaseModalComponent>

      <InvoicesTable clientUid={clientUid} />
    </Box>
  );
};
